import { create } from 'zustand';
import { RunReportParams, RunReportVersionParams, RunReportVersionSummary } from '@/backend/access/runReport/types';
import { ReportValues, RunReportContext } from '@/reports/types';
import { AvailableReport } from '@/types/reportBundleTypes';
import { RunReportStateResponse, RunReportTemplateOption } from '@/types/runReportTypes';
import { RunReportFrontendController } from '../controllers/runReportFrontendController';

export interface RunReportState {
  run: RunReportStateResponse['run'];
  canEdit: boolean;
  runReport: RunReportParams | null;
  versions: RunReportVersionSummary[];
  context: RunReportContext | null;
  /** the bundle version behind the report's key right now; null for a built-in */
  bundleVersionId: string | null;
  /** the coded report template on the run's procedure template, to create the report from; null when there is none */
  reportTemplate: RunReportTemplateOption | null;
}

interface RunReportStore {
  stateByRun: Record<string, RunReportState>;
  /** runs the viewer cannot see, or that do not exist */
  missingRuns: Record<string, true>;
  versionsById: Record<string, RunReportVersionParams>;
  /** the reports a report template can make; null until fetched */
  definitions: AvailableReport[] | null;

  fetchState: (runId: string) => Promise<void>;
  fetchDefinitions: () => Promise<AvailableReport[]>;
  /** create the run's report from the coded report template on its procedure */
  create: (runId: string) => Promise<boolean>;
  saveValues: (runId: string, values: ReportValues) => Promise<boolean>;
  remove: (runId: string) => Promise<boolean>;
  publish: (runId: string, note?: string) => Promise<RunReportVersionParams | null>;
  fetchVersion: (runId: string, versionId: string) => Promise<RunReportVersionParams | null>;
  /** presigned links for files on the run, for the document's figures */
  fileUrls: (runId: string, fileIds: string[]) => Promise<Record<string, string>>;
  reset: () => void;
}

const initialState = {
  stateByRun: {} as Record<string, RunReportState>,
  missingRuns: {} as Record<string, true>,
  versionsById: {} as Record<string, RunReportVersionParams>,
  definitions: null as AvailableReport[] | null,
};

export const useRunReportStore = create<RunReportStore>((set, get) => ({
  ...initialState,

  fetchState: async (runId) => {
    const response = await new RunReportFrontendController().readByRunId(runId);
    if (response.status !== 200) {
      if (response.status === 404) set({ missingRuns: { ...get().missingRuns, [runId]: true } });
      return;
    }
    set({
      stateByRun: {
        ...get().stateByRun,
        [runId]: {
          run: response.run,
          canEdit: response.canEdit,
          runReport: response.runReport,
          versions: response.versions,
          context: response.context,
          bundleVersionId: response.bundleVersionId ?? null,
          reportTemplate: response.reportTemplate ?? null,
        },
      },
    });
  },

  fetchDefinitions: async () => {
    const response = await new RunReportFrontendController().listDefinitions();
    const definitions = response.status === 200 ? response.reports : [];
    set({ definitions });
    return definitions;
  },

  create: async (runId) => {
    const response = await new RunReportFrontendController().create({ runId });
    if (response.status !== 201 || !response.runReport) return false;
    await get().fetchState(runId);
    return true;
  },

  saveValues: async (runId, values) => {
    const current = get().stateByRun[runId];
    if (!current?.runReport) return false;
    const response = await new RunReportFrontendController().updateValues(current.runReport.id, { values });
    if (response.status !== 200 || !response.runReport) return false;
    set({ stateByRun: { ...get().stateByRun, [runId]: { ...current, runReport: response.runReport } } });
    return true;
  },

  remove: async (runId) => {
    const current = get().stateByRun[runId];
    if (!current?.runReport) return false;
    const response = await new RunReportFrontendController().delete(current.runReport.id);
    if (response.status !== 200) return false;
    set({ stateByRun: { ...get().stateByRun, [runId]: { ...current, runReport: null, versions: [], context: null } } });
    return true;
  },

  publish: async (runId, note) => {
    const current = get().stateByRun[runId];
    if (!current?.runReport) return null;
    const response = await new RunReportFrontendController().publish(current.runReport.id, { note });
    if (response.status !== 201 || !response.version) return null;
    const version = response.version;
    const { values: _values, context: _context, ...summary } = version;
    set({
      versionsById: { ...get().versionsById, [version.id]: version },
      stateByRun: { ...get().stateByRun, [runId]: { ...current, versions: [summary, ...current.versions] } },
    });
    return version;
  },

  fetchVersion: async (runId, versionId) => {
    const cached = get().versionsById[versionId];
    if (cached) return cached;
    const current = get().stateByRun[runId];
    if (!current?.runReport) return null;
    const response = await new RunReportFrontendController().readVersion(current.runReport.id, versionId);
    if (response.status !== 200 || !response.version) return null;
    set({ versionsById: { ...get().versionsById, [versionId]: response.version } });
    return response.version;
  },

  fileUrls: async (runId, fileIds) => {
    const current = get().stateByRun[runId];
    if (!current?.runReport || fileIds.length === 0) return {};
    const response = await new RunReportFrontendController().fileUrls(current.runReport.id, fileIds);
    return response.status === 200 ? response.urls : {};
  },

  reset: () => set({ ...initialState }),
}));
